import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Palette } from "lucide-react";
import { useTheme, themeLabel } from "../lib/theme";

type Props = {
  /** When true, only the icon is shown (sidebar collapsed mode). */
  collapsed?: boolean;
};

const MENU_GAP = 6;
const MENU_MIN_WIDTH = 180;

export function ThemePicker({ collapsed = false }: Props) {
  const { theme, setTheme, options } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Drop the cached position whenever the menu closes, so the next open
  // always re-measures against the freshly-rendered menu and stays hidden
  // until that measurement lands (see the visibility toggle below).
  useEffect(() => {
    if (!open) setMenuPos(null);
  }, [open]);

  // Position the portal-rendered menu against the trigger's screen rect.
  // The collapsed sidebar has `overflow-y: auto`, which (per CSS spec) makes
  // overflow-x compute to `auto` too — so a regular `position: absolute`
  // popover positioned outside the rail gets clipped. Portaling to <body>
  // and using `position: fixed` sidesteps that.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const measure = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight ?? 220;
      if (collapsed) {
        // Anchor to the right edge of the rail, vertically aligned with the
        // trigger's bottom. Flip below if it would overflow the viewport.
        const wantedTop = r.bottom - menuH;
        const top = Math.max(8, Math.min(wantedTop, window.innerHeight - menuH - 8));
        setMenuPos({ top, left: r.right + MENU_GAP, width: MENU_MIN_WIDTH });
      } else {
        // Expanded sidebar: open upwards above the trigger, full trigger width.
        setMenuPos({ top: r.top - menuH - MENU_GAP, left: r.left, width: r.width });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, collapsed]);

  const label = themeLabel(theme);

  // Render as soon as it's open (not only once positioned) so menuRef exists
  // for the height measurement on the very first open. Until menuPos lands it
  // sits offscreen + hidden, so it never flashes at the wrong spot.
  const menu = open ? createPortal(
    <ul
      role="listbox"
      ref={menuRef}
      className="theme-picker-menu"
      aria-label="Choose theme"
      style={{
        position: "fixed",
        top: menuPos ? menuPos.top : -9999,
        left: menuPos ? menuPos.left : -9999,
        width: menuPos ? menuPos.width : MENU_MIN_WIDTH,
        minWidth: MENU_MIN_WIDTH,
        visibility: menuPos ? "visible" : "hidden",
      }}
    >
      {options.map((opt) => {
        const active = opt.id === theme;
        return (
          <li key={opt.id}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              className={active ? "active" : undefined}
              onClick={() => { setTheme(opt.id); setOpen(false); }}
              title={themeLabel(opt.id)}
            >
              <span className="theme-picker-check">
                {active && <Check className="icon-svg" />}
              </span>
              {/* Farbtupfer aus dem fremden Thema — der Name allein sagt
                  niemandem, wie Frappé neben Macchiato aussieht. */}
              <span className="theme-picker-dots" data-theme={opt.id} aria-hidden="true">
                <span style={{ background: "var(--bg)" }} />
                <span style={{ background: "var(--accent)" }} />
                <span style={{ background: "var(--info-3)" }} />
              </span>
              <span>{opt.label}</span>
            </button>
          </li>
        );
      })}
    </ul>,
    document.body,
  ) : null;

  return (
    <div
      ref={rootRef}
      className="theme-picker"
      style={{ ["--sidebar-color" as string]: "var(--accent-2)" } as CSSProperties}
    >
      <button
        ref={triggerRef}
        type="button"
        className="theme-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? `Theme: ${label}` : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="icon"><Palette className="icon-svg" /></span>
        <span>{label}</span>
      </button>
      {menu}
    </div>
  );
}
